// =============================================================================
// BusinessBlock — KPIs de negocio para GESTOR_SEDE y ADMIN
// Incluye: facturación, devoluciones, deuda, tasa aceptación, gráficas.
//
// v1.2.1: reorganización visual para evitar que la gráfica de "Presupuestos
// creados últimos 12 meses" quede desproporcionadamente grande.
// Layout anterior:
//   [Donut] | [Facturación 12m]
//   [Presupuestos 12m (full-width, enorme)]
// Layout nuevo:
//   [Donut distribución] (en fila propia, compacto por SVG de tamaño fijo)
//   [Facturación 12m] | [Presupuestos creados 12m] (mismo grid 2 cols)
// Así ambas gráficas de líneas quedan al mismo tamaño que Facturación tenía
// antes: proporcionado y legible.
// =============================================================================

import type { Business } from '../../types/dashboard';
import { KpiCard } from './KpiCard';
import { StatusDonutChart } from './charts/StatusDonutChart';
import { MonthlyLineChart } from './charts/MonthlyLineChart';

interface BusinessBlockProps {
  data: Business | null;
  loading: boolean;
}

function formatMoney(n: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatMoneyCompact(n: number): string {
  if (Math.abs(n) >= 1000) {
    return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k€`;
  }
  return `${n.toFixed(0)}€`;
}

export function BusinessBlock({ data, loading }: BusinessBlockProps) {
  return (
    <section className="space-y-4">
      <header className="flex items-center gap-2">
        <span className="material-symbols-outlined text-primary">insights</span>
        <h2 className="text-base font-semibold text-slate-900">Negocio</h2>
      </header>

      {/* KPIs económicos */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          icon="savings"
          label="Facturación del periodo"
          value={loading ? '' : formatMoney(data?.revenue ?? 0)}
          tone="green"
          loading={loading}
        />
        <KpiCard
          icon="request_quote"
          label="Deuda abierta"
          value={loading ? '' : formatMoney(data?.openDebt ?? 0)}
          subLabel="Pagos pendientes"
          tone={(data?.openDebt ?? 0) > 0 ? 'amber' : 'slate'}
          loading={loading}
        />
        <KpiCard
          icon="undo"
          label="Devuelto al paciente"
          value={loading ? '' : formatMoney(data?.refundsPaid ?? 0)}
          subLabel="En el periodo"
          tone="violet"
          loading={loading}
        />
        <KpiCard
          icon="pending_actions"
          label="Pendiente de devolver"
          value={loading ? '' : formatMoney(data?.refundsPending ?? 0)}
          subLabel="Saldo a favor del paciente"
          tone={(data?.refundsPending ?? 0) > 0 ? 'violet' : 'slate'}
          loading={loading}
        />
      </div>

      {/* KPIs de actividad */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard
          icon="description"
          label="Presupuestos creados"
          value={data?.budgetsCreated ?? 0}
          tone="primary"
          loading={loading}
        />
        <KpiCard
          icon="check_circle"
          label="Tasa de aceptación"
          value={
            loading
              ? ''
              : `${(data?.acceptanceRate.percent ?? 0).toFixed(1)}%`
          }
          subLabel={
            data
              ? `${data.acceptanceRate.accepted} / ${data.acceptanceRate.sent} enviados`
              : undefined
          }
          tone="green"
          loading={loading}
        />
        <KpiCard
          icon="timer"
          label="Tiempo medio aceptación"
          value={
            loading
              ? ''
              : data?.avgDaysToAccept === null || data?.avgDaysToAccept === undefined
                ? '—'
                : `${data.avgDaysToAccept.toFixed(1)} d`
          }
          tone="slate"
          loading={loading}
        />
      </div>

      {/* Donut distribución (fila propia, compacto) */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">
          Distribución por estado
        </h3>
        {loading ? (
          <div className="h-[180px] bg-slate-50 rounded animate-pulse" />
        ) : (
          <StatusDonutChart
            data={
              data?.statusDistribution ?? {
                BORRADOR: 0, ENVIADO: 0, ACEPTADO: 0, RECHAZADO: 0,
              }
            }
          />
        )}
      </div>

      {/* Dos gráficas de líneas al mismo tamaño (grid 2 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          {loading ? (
            <>
              <div className="h-4 w-40 bg-slate-100 rounded animate-pulse mb-4" />
              <div className="h-[180px] bg-slate-50 rounded animate-pulse" />
            </>
          ) : (
            <MonthlyLineChart
              title="Facturación últimos 12 meses"
              data={data?.revenueMonthly ?? []}
              color="#22c55e"
              formatValue={formatMoneyCompact}
            />
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          {loading ? (
            <>
              <div className="h-4 w-48 bg-slate-100 rounded animate-pulse mb-4" />
              <div className="h-[180px] bg-slate-50 rounded animate-pulse" />
            </>
          ) : (
            <MonthlyLineChart
              title="Presupuestos creados últimos 12 meses"
              data={data?.budgetsCreatedTrend ?? []}
              color="#008DD0"
              formatValue={(v) => String(Math.round(v))}
            />
          )}
        </div>
      </div>
    </section>
  );
}
